import * as Yup from "yup";

import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
  Link,
  Typography,
} from "@material-ui/core";
import { Form, Formik, FormikErrors, FormikHelpers } from "formik";
import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  emailLocalLength,
  emailMaxLength,
  emailMinLength,
  nameMaxLength,
  nameMinLength,
} from "../../config/profileFieldLengths";
import {
  hasLowerCase,
  hasNumbers,
  hasSpecialCharacters,
  hasUpperCase,
  maxLength,
  minLength,
} from "../../utils/passwordRules";

import Alert from "@material-ui/lab/Alert";
import CommonLinkedMessage from "../common/LinkedMessage";
import FormButton from "../common/FormButton";
import FormDOBInput from "../common/FormDOBInput";
import FormInput from "../common/FormInput";
import FormInputCheckbox from "../common/FormInputCheckbox";
import FormInputNewPassword from "../common/FormInputNewPassword";
import FormInputPassword from "../common/FormInputPassword";
import LinkedMessage from "../universal-login/LinkedMessage";
import Logo from "../common/Logo";
import PasswordStrengthIndicator from "../common/PasswordStrengthIndicator";
import { ageOfConsentMap } from "../../config/ageOfConsentMap";
import auth0 from "auth0-js";
import auth0Config from "../../utils/auth0ConfigHelper";
import getAge from "../../utils/getAge";
import { getLocaleFromCookie } from "../../utils/getLocaleFromCookie";
import { pushCustomEvent } from "../../utils/customEventHelper";
import { shouldRenderCaptcha } from "../../utils/captcha";
import theme from "../../common/theme";

export const currentYear = new Date().getFullYear();
export const earliestPlausibleYear = currentYear - 120;

type ValidationSchema = (t: Function) => Yup.ObjectSchema<{
  termsApproved: boolean;
  dob: Date | null;
  year: number;
  month: number;
  day: number;
  consentRequired: boolean;
  email: string;
  parentEmail: string;
  emailSubscribed: boolean;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
}>;

const validationSchema: ValidationSchema = (t) =>
  Yup.object().shape({
    termsApproved: Yup.boolean().oneOf(
      [true],
      `${t("error.validation.terms")}`,
    ),
    dob: Yup.date().required(t("error.validation.dob.required")).nullable(),
    year: Yup.number()
      .min(earliestPlausibleYear, `${earliestPlausibleYear}-${currentYear}`)
      .max(currentYear, `${earliestPlausibleYear}-${currentYear}`)
      .required(t("error.validation.year.required")),
    month: Yup.number()
      .min(1, "1-12")
      .max(12, "1-12")
      .required(t("error.validation.month.required")),
    day: Yup.number()
      .min(1, "1-31")
      .max(31, "1-31")
      .required(t("error.validation.day.required")),
    consentRequired: Yup.boolean(),
    email: Yup.string()
      .email(t("error.validation.email.invalid"))
      .required(t("error.validation.email.required"))
      .min(
        emailMinLength,
        t("error.validation.newEmail.policy.lengthAtLeast", {
          n: emailMinLength,
        }),
      )
      .max(
        emailMaxLength,
        t("error.validation.newEmail.policy.lengthAtMost", {
          n: emailMaxLength,
        }),
      )
      .test(
        "localLengthCheck",
        t("error.validation.newEmail.policy.localLengthAtMost", {
          n: emailLocalLength,
        }),
        (value: string) => {
          if (!value) {
            return false;
          }

          const [localPart] = value.split("@");

          if (localPart.length > emailLocalLength) {
            return false;
          }

          return true;
        },
      ),
    parentEmail: Yup.string()
      .when("consentRequired", {
        is: true,
        then: Yup.string()
          .email(t("error.validation.parentEmail.invalid"))
          .required(t("error.validation.parentEmail.required")),
        otherwise: Yup.string().email().notRequired(),
      })
      .notOneOf(
        [Yup.ref("email"), null],
        t("error.validation.parentEmail.invalid"),
      )
      .min(
        emailMinLength,
        t("error.validation.newEmail.policy.lengthAtLeast", {
          n: emailMinLength,
        }),
      )
      .max(
        emailMaxLength,
        t("error.validation.newEmail.policy.lengthAtMost", {
          n: emailMaxLength,
        }),
      )
      .test(
        "localLengthCheck",
        t("error.validation.newEmail.policy.localLengthAtMost", {
          n: emailLocalLength,
        }),
        (value: string) => {
          if (!value) {
            return true;
          }

          const [localPart] = value.split("@");

          if (localPart.length > emailLocalLength) {
            return false;
          }

          return true;
        },
      ),
    emailSubscribed: Yup.boolean(),
    firstName: Yup.string()
      .required(t("error.validation.firstName.required"))
      .min(
        nameMinLength,
        t("error.validation.firstName.policy.lengthAtLeast", {
          n: nameMinLength,
        }),
      )
      .max(
        nameMaxLength,
        t("error.validation.firstName.policy.lengthAtMost", {
          n: nameMaxLength,
        }),
      ),
    lastName: Yup.string()
      .required(t("error.validation.lastName.required"))
      .min(
        nameMinLength,
        t("error.validation.lastName.policy.lengthAtLeast", {
          n: nameMinLength,
        }),
      )
      .max(
        nameMaxLength,
        t("error.validation.lastName.policy.lengthAtMost", {
          n: nameMaxLength,
        }),
      ),
    password: Yup.string()
      .required(t("error.validation.newPassword.required"))
      .concat(minLength(t))
      .concat(maxLength(t))
      .concat(hasUpperCase(t))
      .concat(hasLowerCase(t))
      .concat(hasNumbers(t))
      .concat(hasSpecialCharacters(t)),
    confirmPassword: Yup.string()
      .oneOf(
        [Yup.ref("password"), null],
        t("error.validation.newPassword.mustMatch"),
      )
      .required(t("error.validation.newPassword.required"))
      .concat(minLength(t))
      .concat(maxLength(t))
      .concat(hasUpperCase(t))
      .concat(hasLowerCase(t))
      .concat(hasNumbers(t))
      .concat(hasSpecialCharacters(t)),
  });

type RegisterFormProps = {};

type RegistrationHint = {
  locale: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  orderId?: string;
};

interface InitialValues {
  dob: Date;
  year: string;
  month: string;
  day: string;
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  consentRequired: boolean;
  parentEmail: string;
  termsApproved: boolean;
  emailSubscribed: boolean;
  directMarketing: boolean;
  auth0Response: string;
}

type FocusFirstError = (errors: FormikErrors<InitialValues>) => void;

const focusFirstError: FocusFirstError = (errors) => {
  const {
    email,
    password,
    confirmPassword,
    firstName,
    lastName,
    day,
    month,
    year,
    termsApproved,
    parentEmail,
  } = errors;

  try {
    if (email) {
      document.getElementsByName("email")[0].focus();
    } else if (password) {
      document.getElementsByName("password")[0].focus();
    } else if (confirmPassword) {
      document.getElementsByName("confirmPassword")[0].focus();
    } else if (firstName) {
      document.getElementsByName("firstName")[0].focus();
    } else if (lastName) {
      document.getElementsByName("lastName")[0].focus();
    } else if (day) {
      document.getElementsByName("day")[0].focus();
    } else if (month) {
      document.getElementsByName("month")[0].focus();
    } else if (year) {
      document.getElementsByName("year")[0].focus();
    } else if (termsApproved) {
      document.getElementsByName("termsApproved")[0].focus();
    } else if (parentEmail) {
      document.getElementsByName("parentEmail")[0].focus();
    }
  } catch (error) {
    console.error(error);
  }
};

type FocusOnErrorprops = {
  submitCount: number;
  isValid: boolean;
  errors: FormikErrors<InitialValues>;
  isSubmitting: boolean;
};

const FocusOnError: React.FC<FocusOnErrorprops> = ({
  isValid,
  submitCount,
  errors,
  isSubmitting,
}) => {
  useEffect(() => {
    if (submitCount > 0 && !isSubmitting && !isValid) {
      focusFirstError(errors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCount, isSubmitting]);
  return null;
};

const RegisterForm: React.FC<RegisterFormProps> = () => {
  const { t } = useTranslation();

  const initialDate = new Date();

  const login_hint = auth0Config?.extraParams?.login_hint;
  const locale = getLocaleFromCookie() ?? "en_??";
  let firstName: string | undefined;
  let lastName: string | undefined;
  let email: string | undefined;
  let orderId: string | undefined;
  if (login_hint) {
    ({ firstName, lastName, email, orderId } = JSON.parse(
      login_hint,
    ) as RegistrationHint);
  }
  const [, country] = locale.split("_");
  const minAge = ageOfConsentMap[country] || ageOfConsentMap.ROW;
  const redirectURL = new URL(auth0Config.callbackURL);
  if (redirectURL.host.includes("mywarhammer.com")) {
    redirectURL.searchParams.set("locale", getLocaleFromCookie() ?? "en_??");
  }
  const params = {
    overrides: {
      __tenant: auth0Config.auth0Tenant,
      __token_issuer: auth0Config.authorizationServer.issuer,
    },
    domain: auth0Config.auth0Domain,
    clientID: auth0Config.clientID,
    redirectUri: redirectURL.toString(),
    responseType: "code",
    ...auth0Config.internalOptions,
  };
  const webAuth = new auth0.WebAuth(params);
  const [captcha, setCaptcha] = useState<undefined | auth0.Captcha>();

  useEffect(() => {
    const captchaContainer = document.querySelector(".captcha-container");
    if (shouldRenderCaptcha(captchaContainer)) {
      setCaptcha(webAuth.renderCaptcha(captchaContainer as HTMLElement));
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document.querySelector(".captcha-container")]);

  const initialValues: InitialValues = {
    dob: initialDate,
    year: "",
    month: "",
    day: "",
    email: email || "",
    password: "",
    confirmPassword: "",
    firstName: firstName || "",
    lastName: lastName || "",
    consentRequired: false,
    parentEmail: "",
    termsApproved: false,
    emailSubscribed: false,
    directMarketing: false,
    auth0Response: "",
  };

  type HandleSubmit = (
    values: InitialValues,
    actions: FormikHelpers<InitialValues>,
  ) => Promise<void>;

  const handleSubmit: HandleSubmit = async (values, actions) => {
    const {
      dob,
      email,
      password,
      firstName,
      lastName,
      emailSubscribed,
      termsApproved,
      parentEmail,
      directMarketing,
    } = values;

    const userAge = getAge(new Date(), dob);

    if (userAge < minAge && values.consentRequired === false) {
      actions.setValues({
        ...values,
        consentRequired: true,
      });
      actions.setTouched({});
      return;
    }

    try {
      pushCustomEvent("registration");
      webAuth.signup(
        {
          connection: "Username-Password-Authentication",
          email,
          password,
          userMetadata: {
            firstName,
            lastName,
            parentEmail,
            termsApproved: termsApproved.toString(),
            dateOfBirth: dob.getTime().toString(),
            emailSubscribed: emailSubscribed.toString(),
            locale,
            directMarketing: directMarketing.toString(),
            orderId,
          },
          captcha: captcha?.getValue(),
        } as auth0.DbSignUpOptions,
        function (err) {
          if (err) {
            captcha?.reload((error) => {
              if (error) {
                actions.setErrors({
                  auth0Response: t("error.unspecific"),
                });
              }
            });
            const { code } = err;
            actions.setErrors({
              auth0Response: code,
              password:
                code === "invalid_password"
                  ? t("error.invalid_password")
                  : undefined,
            });
          } else {
            webAuth.login(
              {
                realm: "Username-Password-Authentication",
                username: values.email,
                password: values.password,
                captcha: captcha?.getValue(),
              } as auth0.LoginOptions,
              (error) => {
                if (error) {
                  captcha?.reload((error) => {
                    if (error) {
                      actions.setErrors({
                        auth0Response: t("error.unspecific"),
                      });
                    }
                  });
                }
              },
            );
          }
        },
      );
    } catch (error) {
      captcha?.reload(() => {});
      actions.setErrors({
        auth0Response: t("error.unspecific"),
      });
    }

    actions.setSubmitting(false);
  };

  return (
    <Formik
      validationSchema={validationSchema(t)}
      validateOnMount={false}
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {({
        values,
        isSubmitting,
        errors,
        isValid,
        submitCount,
      }): JSX.Element => {
        return (
          <Card>
            <Form noValidate data-testid="registrationForm">
              <Box
                display="flex"
                alignItems="center"
                alignContent="center"
                justifyContent="center"
                mt={3}
                mb={1}
              >
                <Logo />
              </Box>
              <CardHeader
                data-testid="signupTitle"
                titleTypographyProps={{
                  align: "center",
                  gutterBottom: false,
                  paragraph: false,
                  variant: "h1",
                }}
                title={t("register.title")}
              />

              <CardContent>
                <Grid
                  container
                  direction="column"
                  justify="center"
                  alignContent="center"
                  alignItems="center"
                >
                  <Grid item>
                    <Typography
                      align="center"
                      variant="body1"
                      component="p"
                      data-testid="existingAccountText"
                      paragraph
                    >
                      <LinkedMessage
                        i18nKey="register.existingAccountLink"
                        to="login"
                        testId="existingAccountLink"
                        gtmEvent="loginLink"
                      />
                    </Typography>
                  </Grid>

                  <Grid
                    container
                    direction="column"
                    alignContent="stretch"
                    wrap="nowrap"
                    spacing={2}
                  >
                    <Grid item>
                      <FormInput
                        type="email"
                        name="email"
                        label={t("label.email")}
                      />
                    </Grid>
                    <Grid item>
                      <FormInputNewPassword
                        name="password"
                        label={t("passwordManagement.newPassword")}
                      />
                    </Grid>
                    <Grid item>
                      <PasswordStrengthIndicator />
                    </Grid>
                    <Grid item>
                      <FormInputPassword
                        name="confirmPassword"
                        label={t("passwordManagement.confirmNewPassword")}
                      />
                    </Grid>
                    <Grid item>
                      <FormInput
                        type="text"
                        name="firstName"
                        label={t("register.firstName")}
                      />
                    </Grid>
                    <Grid item>
                      <FormInput
                        type="text"
                        name="lastName"
                        label={t("register.lastName")}
                      />
                    </Grid>
                    <Grid item>
                      {values.consentRequired ? (
                        <Grid
                          container
                          direction="column"
                          alignContent="stretch"
                          wrap="nowrap"
                          spacing={2}
                        >
                          <Grid item>
                            <FormInput
                              type="email"
                              name="parentEmail"
                              label={t("register.parentEmail")}
                            />
                          </Grid>
                          <Grid item>
                            <Alert severity="info">
                              <Trans
                                i18nKey="register.parentEmailRequired"
                                values={{ minAge }}
                              ></Trans>
                            </Alert>
                          </Grid>
                        </Grid>
                      ) : (
                        <FormDOBInput />
                      )}
                    </Grid>
                    <Grid item>
                      <FormInputCheckbox
                        name="termsApproved"
                        label={
                          <CommonLinkedMessage
                            i18nKey="register.terms"
                            to="https://www.mywarhammer.com/termsandconditions"
                            target="_blank"
                            testId="termsLink"
                            rel="noopener"
                          />
                        }
                      />
                    </Grid>

                    <Grid item>
                      <FormInputCheckbox
                        name="emailSubscribed"
                        label={t("register.newsletter.label")}
                      />
                      <Typography
                        style={{ marginTop: 6 }}
                        variant="body2"
                        data-testid="emailSubscribedText"
                      >
                        <LinkedMessage
                          i18nKey="register.newsletter.fieldHelper"
                          to="login"
                          testId="emailSubscribedLink"
                        />
                      </Typography>
                    </Grid>

                    <Grid item>
                      <FormInputCheckbox
                        name="directMarketing"
                        data-testid="directMarketing"
                        label={t("register.directMarketing.label")}
                      />
                      <Typography
                        variant="body2"
                        data-testid="directMarketingText"
                      >
                        {t("register.directMarketing.fieldHelper")}
                      </Typography>
                    </Grid>

                    <Grid item>
                      <Typography variant="body1" component="p">
                        <Trans
                          i18nKey="register.privacy"
                          components={{
                            Link: (
                              <Link
                                href="https://www.warhammer.com/legal#Privacy%20Notice"
                                target="_blank"
                                rel="noopener"
                                underline="always"
                                data-testid="privacyLink"
                              ></Link>
                            ),
                          }}
                        />
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>

              <CardActions>
                <div className="captcha-container"></div>
              </CardActions>

              <CardActions>
                <Grid
                  container
                  direction="row"
                  justify="flex-end"
                  alignItems="center"
                  style={{ marginTop: theme.spacing(2) }}
                >
                  <Grid item>
                    <FormButton
                      waiting={isSubmitting}
                      label={t("button.continue")}
                      testId="sendButton"
                    />
                  </Grid>
                </Grid>
              </CardActions>

              <FocusOnError
                submitCount={submitCount}
                isValid={isValid}
                errors={errors}
                isSubmitting={isSubmitting}
              />

              {errors.auth0Response && (
                <Alert
                  severity="error"
                  data-testid="generalError"
                  style={{ margin: 16 }}
                >
                  {errors.auth0Response === "invalid_password" ? (
                    t("newPassword.invalid")
                  ) : (
                    <CommonLinkedMessage
                      i18nKey="register.error"
                      to="https://www.warhammer.com/contact-us"
                      testId="generalErrorLinkMessage"
                      target="_blank"
                      rel="noopener"
                    />
                  )}
                </Alert>
              )}
            </Form>
          </Card>
        );
      }}
    </Formik>
  );
};

export default RegisterForm;
